import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useEffect } from 'react';
import { runSharedRedirectFromCfmHref } from '../util/sharedRedirectFromCfmHref';

type LinkType = 'SHARED_REDIRECT' | 'NORMAL' | null;

/**
 * Anchors (or an ancestor) with this attribute skip getSharedRedirect and use native navigation.
 * Ignored when href contains index.cfm — those always use the shared-redirect flow.
 */
export const SKIP_SHARED_REDIRECT_ATTR = 'data-skip-shared-redirect';

interface LinkInterceptorContextType {
  checkHrefType: (href: string | null) => LinkType;
}

const LinkInterceptorContext = createContext<
  LinkInterceptorContextType | undefined
>(undefined);

interface ProviderProps {
  children: ReactNode;
}

const checkHrefType = (href: string | null): LinkType => {
  if (!href) return null;
  // Treat links containing index.cfm as shared-redirect links
  return href.includes('index.cfm') ? 'SHARED_REDIRECT' : 'NORMAL';
};

export const LinkInterceptorProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');

      if (
        anchor.closest(`[${SKIP_SHARED_REDIRECT_ATTR}]`) &&
        !href?.includes('index.cfm')
      ) {
        return;
      }

      const result = checkHrefType(href);
      if (result === 'SHARED_REDIRECT' && href) {
        // For index.cfm links: stop the browser's default navigation
        event.preventDefault();

        void runSharedRedirectFromCfmHref(href, { kind: 'new-tab' });
      } else if (result === 'NORMAL') {
        // Non index.cfm links behave as normal (no preventDefault)
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <LinkInterceptorContext.Provider value={{ checkHrefType }}>
      {children}
    </LinkInterceptorContext.Provider>
  );
};

export const useLinkInterceptor = (): LinkInterceptorContextType => {
  const context = useContext(LinkInterceptorContext);

  if (!context) {
    throw new Error(
      'useLinkInterceptor must be used within LinkInterceptorProvider'
    );
  }

  return context;
};
